// Template blanks: "[$X]", "[30]", "[Payment period]" — the parts a company
// fills in. Each blank is shown on its own with the sentence it sits in, so
// nobody has to hunt through the text for brackets.

export interface TemplateBlank {
  /** Exactly as written, brackets and all: "[$X]". */
  blank: string;
  /** Inside the brackets: "$X". */
  label: string;
  /** The sentence around it, with the blank shown as ___. */
  context: string;
  /** A starting value when the blank is really a suggested number ("[30]", "[10%]", "[1 year]"). */
  suggestion: string;
}

const BLANK = /\[[^\]\n]+\]/g;

export function templateBlanks(body: string): TemplateBlank[] {
  const seen = new Set<string>();
  const out: TemplateBlank[] = [];
  for (const line of body.split(/\r?\n/)) {
    for (const m of line.matchAll(BLANK)) {
      const blank = m[0];
      if (seen.has(blank)) continue;
      seen.add(blank);
      const label = blank.slice(1, -1).trim();
      const at = m.index ?? 0;
      const clean = (s: string) => s.replace(/^##\s+|^-\s+|^!!\s+/, "").replace(BLANK, (b) => (b === blank ? "___" : b.slice(1, -1)));
      let context = clean(line);
      if (context.length > 140) {
        const pos = clean(line.slice(0, at)).length;
        const from = Math.max(0, pos - 70);
        context = `${from > 0 ? "…" : ""}${context.slice(from, from + 140)}${from + 140 < context.length ? "…" : ""}`;
      }
      // A short value with a digit and no "X" reads as a suggestion: [30], [10%], [1.5%], [2-4], [1 year].
      const suggestion = label.length <= 16 && /\d/.test(label) && !/x/i.test(label) && !label.includes("/") ? label : "";
      out.push({ blank, label, context: context.trim(), suggestion });
    }
  }
  return out;
}

/** Put the values in. Blanks left empty stay as they are. */
export function fillBlanks(body: string, values: Record<string, string>): string {
  return body.replace(BLANK, (b) => {
    const v = values[b];
    return v !== undefined && v.trim() ? v.trim() : b;
  });
}
